import { useState, useRef } from "react"
import { uploadExcelAPI } from "../services/uploadExcelAPI"
import { Button } from "../components/ui/buttons/Button"
import { useLoading } from "../contexts/LoadingContext"
import { useError } from "../contexts/ErrorContext"
import { useUnsavedChanges } from "../contexts/UnsavedChangesContext"

/**
 * Hook that provides a function for importing excel data into the front end, and error information
 * 
 * @returns {function} FileUploadButton - button component
 * @returns {string} fileName
 * @return {JSX.Element} ImportColumnsMissingList - displays names of columns missing from the excel sheet
 * @returns {function} setMissingColumns - when data submitted in component, should reset this state
 * 
 * @category Hooks
 */
export const useImportExcel = () => {

  //set state
  const [missingColumns, setMissingColumns] = useState([])
  const fileInputRef = useRef(null)
  const [fileName, setFileName] = useState(null)
  const { showLoading, hideLoading } = useLoading()
  const { showError } = useError()
  const { setHasUnsavedChanges } = useUnsavedChanges()

  /**
   * function that handles import
   * 
   * @param {*} e - event that contains excel file
   * @param {function} setData - setter function that updates data in component
   * @param {string} route - API route to process excel sheet
   * @param {Object} columnMap - maps database item names to column names, eg. {"Site_Name": "Site Name"}, for formatting column names from excel
   * @param {Object} schema - object containing the columns we expect on the table, what input the corresponding table cell should have, eg. {"Site_Name": {"inputType": "text", "title": "Site Name"}}
   * @param {Object[]} data - needed to check for existing data - user must clear it out to import new data
   * @param {Object[]} agmOptions - agm dropdown options, helps derive US/DS sites for subchains
   * @param {number} JobID
   * 
   */

  const handleImportExcel = async (e, setData, setOriginalData = null, route, columnMap, schema, data, agmOptions, JobID) => {
    //Quick fix to keep from duplicating agm/subchain points
    if (data?.length > 1) {
      let markerType = route === 'agms' ? 'AGMs' : 'Subchains';
      showError(`Please Remove ${markerType} Before Importing New Ones`);
      return;
    }

    try {
      setMissingColumns([])
      setFileName(e.target.files[0].name);
      showLoading("Loading file...")

      // 1. get file from event, send to backend for processing

      const formData = new FormData();
      formData.append("file", e.target.files[0])

      const response = await uploadExcelAPI(route, formData, JobID)

      if (!response.success) {
        throw new Error("Failed to upload file")
      }

      const data = route === "agms" ? response.data.agmData : response.data

      // 2. format each item in JSON data (equivalent to excel row) to have same key as an item from the database, based on provided columnMap
      // 3. check if we have all the correct columns in resulting JSON, based on the items in the provided schema

      const missingColumns = [];

      //schema = jobControlSchema
      //columnMap = jobControlColumnNameMaps

      function findKeyCaseInsensitive(row, columnTitle) {
        return Object.keys(row).find(key => key.trim().toLowerCase() === columnTitle.trim().toLowerCase());
      }

      const formattedData = data.map((row, i) => {
        const formattedRow = {};

        for (const key of Object.keys(schema)) {

          //get the column name that we are expecting from excel sheet
          const columnTitle = columnMap[key];

          const actualKey = findKeyCaseInsensitive(row, columnTitle);

          //if data has that column, add the data and formatted key to formattedRow
          //if that column is missing from the data, add a blank item to formattedRow (to give blank cell in table), and add the column name to missingColumns

          if (actualKey) {
            formattedRow[key] = row[actualKey];
          } else {
            //if no actualKey from columnTitle, but row has value for key, use that, otherwise leave empty
            formattedRow[key] = row[key] || "";
            if (!missingColumns.includes(columnTitle) && !row[key]) {
              missingColumns.push(columnTitle);
            }
          }

        
          formattedRow.JobID = JobID
        }
        return formattedRow;
      });

      // if subchains route than add the AGMID to US_Site & DS_Site - removes sitename string - Bryan
      if (route === 'subchains' && agmOptions.length > 0) {
        // Create a lookup map from label to value
        const agmMap = new Map(agmOptions.map(agm => [agm.label, agm.value]));

        formattedData.forEach((row, index) => {
          //add default order = as they appear in the imported sheet
          row.OrderNum = index + 1;

          // Ensure US_Site and DS_Site exist (set to null if missing)
          if (!row.US_Site) row.US_Site = null;
          if (!row.DS_Site) row.DS_Site = null;

          // Convert labels to values if matches found in agmMap
          if (agmMap.has(row.US_Site)) row.US_Site = agmMap.get(row.US_Site);
          if (agmMap.has(row.DS_Site)) row.DS_Site = agmMap.get(row.DS_Site);
        });
      }


      // 4. reset data in table to formatted excel data, update errors to include missing columns, set unsavedchanges to true

      //if importing agms, need to return job data for comparison against selectedJob
      if(route === 'agms'){
        setData(formattedData, response.data.jobData)
      } else {
        setData(formattedData)
      }

      if(setOriginalData){
        setOriginalData(structuredClone(formattedData))
      }
      setHasUnsavedChanges(true)
      setMissingColumns(missingColumns)
      hideLoading()

    } catch (error) {
      console.error("Error:", error);

      showError(`Problem importing excel data: ${error.message}`)

      hideLoading()
    }
  }

  // File upload button component

  const FileUploadButton = ({ data, setData, setOriginalData = null, route, columnMap, schema, agmOptions = [], disabled = false, JobID }) => {

    const handleButtonClick = () => {
      fileInputRef.current.click();
    };

    const handleFileChange = (e) => {

      handleImportExcel(e, setData, setOriginalData, route, columnMap, schema, data, agmOptions, JobID);

    };

    return (
      <div className="file-upload-container">
        <Button
          variant="noBackground"
          onClick={handleButtonClick}
          className="text-sm"
          disabled={disabled}
        >
          <i className="fa-solid fa-file-import text-purple-800 mr-2"></i>Import
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      </div>
    );
  };

  const ImportColumnsMissingList = () => {

    if (missingColumns.length === 0) return null

    const message = missingColumns.length === 1 ? "The excel sheet was missing data for the following column:" : "The excel sheet was missing data for the following columns:"

    return (
      <div>
        <span className="text-amber-600 my-3"><i className="fa-solid fa-circle-exclamation mr-2" ></i>{message}{" "} </span>
        {missingColumns.map((error, i) => {
          return (
            <span className="text-amber-600 my-3" key={`import-error-${error}`}>{i === missingColumns.length - 1 ? error : `${error},`} </span>
          )
        })}
      </div>
    )
  }


  return { FileUploadButton, fileName, setFileName, ImportColumnsMissingList, setMissingColumns }
}
